import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'querystring';
import { AppContext } from '@prisjakt/ui-app-context';
import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';
import { FiltersDesktop } from './filters-desktop';
import { FiltersMobile } from './filters-mobile';

import { adsViewUpdate } from 'src/globals/actions';

import {
  Filter,
  InitialFilters,
  SearchFacetType,
} from 'src/areas/search/components/results/types';
import { AppliedFilter } from './applied-filters/types';
import { SearchFacetListItem } from 'src/types';

import * as S from './styles';
import { DesktopOnly, MediumDown } from 'src/ui/block';

const DELIMITER = ',';

type Props = {
  filters: Filter[];
  productsCount: number;
  query: string;
};

export const ResultFilters = (props: Props) => {
  const { filters, productsCount, query } = props;

  const { t } = useContext(AppContext);

  const dispatch = useDispatch();

  const location = useLocation();
  const history = useHistory();

  const getSelectedFromUrl = (facet: Filter) => {
    const qs = queryString.decode(location.search.substr(1));
    const facetFromUrl = qs[facet.id] || '';
    const facetFromUrlString = Array.isArray(facetFromUrl)
      ? facetFromUrl[0]
      : facetFromUrl;

    switch (facet.type) {
      case SearchFacetType.SEARCH_FACET_LIST: {
        return {
          selected: facetFromUrlString.split(',').filter(x => x),
        };
      }
      default: {
        return {};
      }
    }
  };

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<InitialFilters | {}>(
    {}
  );

  // Set filter states from url on page load and on query change
  useEffect(() => {
    const selectedFiltersFromUrl = filters.reduce(
      (all, facet) => ({
        ...all,
        [facet.id.toString()]: {
          id: facet.id,
          ...getSelectedFromUrl(facet),
        },
      }),
      {}
    );

    setSelectedFilters(selectedFiltersFromUrl);

    filters.forEach(filter => {
      switch (filter.type) {
        case SearchFacetType.SEARCH_FACET_LIST: {
          const options = filter.options as (SearchFacetListItem & {
            value: string;
          })[];

          if (selectedFiltersFromUrl[filter.id]?.selected.length) {
            const selected = options.filter(option => {
              return selectedFiltersFromUrl[filter.id].selected.includes(
                option.id
              );
            });

            setAppliedFilters(state => {
              return [
                ...state,
                {
                  filterId: filter.id,
                  type: SearchFacetType.SEARCH_FACET_LIST,
                  title: t(
                    'search',
                    `facets.title-${filter.id}` as keyof ParamsAndReturnTypes['search']
                  ),
                  values: selected.map(s => s.value),
                },
              ];
            });
          } else {
            setAppliedFilters(state => {
              return state.filter(f => f.filterId !== filter.id);
            });
          }
          break;
        }
        default:
          break;
      }
    });
  }, [query]);

  const setSelectedFilter = useCallback(
    (id: string, itemId: string | null, value: string | number) => {
      setAppliedFilters(state => {
        const existingFilter = state.filter(filter => filter.filterId === id);

        if (existingFilter.length) {
          const existingId = existingFilter.filter(
            filter => filter.filterId === id
          )[0];

          // If value is already selected...
          if (existingId && existingId.values.includes(value)) {
            // ...and it's the only existing value, remove the whole existing filter
            if (existingId.values.length === 1) {
              return [...state.filter(filter => filter.filterId !== id)];
            }
            // ...remove existing value
            return [
              ...state.filter(filter => filter.filterId !== id),
              ...existingFilter.map(filter => ({
                ...filter,
                values: existingId.values.filter(val => val !== value),
              })),
            ];
          }

          // Adds value to existing filter
          return [
            ...state.filter(filter => filter.filterId !== id),
            ...existingFilter.map(s => ({
              ...s,
              values: [...s.values, value],
            })),
          ];
        }

        return [
          ...state,
          {
            type: SearchFacetType.SEARCH_FACET_LIST,
            title: t(
              'search',
              `facets.title-${id}` as keyof ParamsAndReturnTypes['search']
            ),
            values: [value],
            filterId: id,
          },
        ];
      });
    },
    []
  );

  if (!filters?.length) return null;

  const updateUrl = (
    type: SearchFacetType,
    id: string,
    newFacets: InitialFilters | {}
  ) => {
    const qs = queryString.decode(location.search.substr(1));

    Object.keys(newFacets).forEach(() => {
      const facet = newFacets[id];

      switch (type) {
        case SearchFacetType.SEARCH_FACET_LIST: {
          if (!facet.selected?.length) {
            delete qs[id];
          } else {
            qs[id] = facet.selected.join(DELIMITER);
          }
          break;
        }
        default:
          break;
      }
    });

    delete qs.offset;

    history.replace(`${location.pathname}?${queryString.encode(qs)}`);
  };

  const onFilterOptionToggle = (
    id: string,
    type: SearchFacetType,
    itemId: string | null,
    values: (string | number)[]
  ) => {
    const newFacets = selectedFilters;

    switch (type) {
      case SearchFacetType.SEARCH_FACET_LIST: {
        if (!itemId) break;
        const parsedId = String(itemId);
        const { selected } = newFacets[id];
        if (selected?.includes(parsedId)) {
          newFacets[id].selected = selected.filter(
            (x: string) => x !== parsedId
          );
        } else {
          newFacets[id].selected = selected?.concat(parsedId);
        }
        break;
      }

      default: {
        break;
      }
    }

    updateUrl(type, id, newFacets);
    setSelectedFilter(id, itemId, values[0]);
    dispatch(adsViewUpdate({ view: 'search_results_recieved' }));
  };

  const clearSingleFilter = (id: string, type: SearchFacetType) => {
    const newFacets = {
      ...selectedFilters,
      [id]: { ...selectedFilters[id], selected: [] },
    };

    setSelectedFilters(newFacets);
    setAppliedFilters(state => state.filter(filter => filter.filterId !== id));
    updateUrl(type, id, newFacets);
  };

  const clearAllFilters = () => {
    const clearedFilters = filters.reduce(
      (all, facet) => ({
        ...all,
        [facet.id.toString()]: {
          id: facet.id,
          selected: [],
        },
      }),
      {}
    );
    const qs = queryString.decode(location.search.substr(1));

    setSelectedFilters(clearedFilters as InitialFilters);
    setAppliedFilters([]);
    // Clears filter query strings from url
    history.replace(
      `${location.pathname}?${queryString.encode({ search: qs.search })}`
    );
  };

  return (
    <S.ResultFilters>
      <DesktopOnly>
        <div>
          <FiltersDesktop
            appliedFilters={appliedFilters}
            clearAllFilters={clearAllFilters}
            clearSingleFilter={clearSingleFilter}
            // We only render filters of type SEARCH_FACET_LIST because the range-filters are too buggy.
            // We should allow range-filters when the range-filter bugs are fixed.
            filters={filters.filter(
              item => item.type === SearchFacetType.SEARCH_FACET_LIST
            )}
            onFilterOptionToggle={onFilterOptionToggle}
            selectedFilters={selectedFilters}
          />
        </div>
      </DesktopOnly>

      <MediumDown>
        <div>
          <FiltersMobile
            appliedFilters={appliedFilters}
            clearAllFilters={clearAllFilters}
            clearSingleFilter={clearSingleFilter}
            // We only render filters of type SEARCH_FACET_LIST because the range-filters are too buggy.
            // We should allow range-filters when the range-filter bugs are fixed.
            filters={filters.filter(
              item => item.type === SearchFacetType.SEARCH_FACET_LIST
            )}
            onFilterOptionToggle={onFilterOptionToggle}
            productsCount={productsCount}
            selectedFilters={selectedFilters}
          />
        </div>
      </MediumDown>
    </S.ResultFilters>
  );
};
